import React from 'react'
import { Box, Grid, IconButton, Typography, Tooltip } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { numberFormat } from './orderSummary'
import { parseDateAndTime, parseTime, formatCurrency } from '../../../helpers/UtilityHelper'
import { isAdmin } from '../../../helpers/ReduxHelpers'
import { NAVY, SUBTLE, BORDER, ERROR, SUCCESS, WARNING } from '../../../styles/foundation/colors'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import SectionCard from './SectionCard'

export const InfoRow = ({ label, value, hide, extra }) => {
  if (hide) return null
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 0.75,
        borderBottom: `1px solid ${BORDER}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: SIZE['2xs'],
          color: SUBTLE,
          fontWeight: WEIGHT.medium,
          flexShrink: 0,
          mr: 2,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          component='span'
          sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.semibold }}
        >
          {value || '\u2014'}
        </Typography>
        {extra}
      </Box>
    </Box>
  )
}

export default function SummaryCards({ orderDetails, channelAccess, insuranceDetails }) {
  if (!orderDetails) return null

  const sd = orderDetails.shipment_detail?.[0]
  const mp = orderDetails.merchant_pricing
  const isB2B = orderDetails.shipment_type === 'B2B'
  const paymentMode = orderDetails.payment_mode || ''

  const volWeight =
    sd && !isB2B && sd.item_length && sd.item_breadth && sd.item_height
      ? ((sd.item_length * sd.item_breadth * sd.item_height) / 5000).toFixed(2)
      : null

  const actualTat = orderDetails.actualDeliveryTat != null ? orderDetails.actualDeliveryTat : null
  const tatColor =
    actualTat != null && mp?.maxTat != null
      ? actualTat <= mp.maxTat ? SUCCESS : ERROR
      : NAVY

  const courierEdd = !orderDetails.terminalStatus && orderDetails.courierEdd ? orderDetails.courierEdd : null

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* Key Dates */}
      <Grid item xs={12} md={4}>
        <SectionCard title='Key Dates' sx={{ mb: 0, height: '100%' }}>
          <InfoRow
            label='Proship Order Creation Date'
            value={orderDetails.order_date ? parseDateAndTime(orderDetails.order_date, true) : null}
          />
          <InfoRow
            label='Pickup Date'
            value={orderDetails.actualPickUpTime ? parseDateAndTime(orderDetails.actualPickUpTime) : null}
          />
          <InfoRow
            label='Original EDD'
            value={orderDetails.edd ? parseDateAndTime(orderDetails.edd, true) : null}
          />
          {(orderDetails.customEdd || orderDetails.kciImpactedEdd) && (
            <InfoRow
              label='Custom EDD'
              value={parseDateAndTime(orderDetails.customEdd || orderDetails.kciImpactedEdd, true)}
            />
          )}

          <InfoRow
            label='Delivery Date'
            value={
              orderDetails.actualDeliveryTime
                ? parseDateAndTime(orderDetails.actualDeliveryTime, true)
                : null
            }
          />
          {mp?.minTat != null && mp?.maxTat != null && (
            <InfoRow
              label='Expected TAT'
              value={
                <>
                  {`${mp.minTat}\u2013${mp.maxTat} days`}
                  {courierEdd && (
                    <Tooltip title={`Courier EDD: ${parseDateAndTime(courierEdd, true)}`}>
                      <IconButton
                        size='small'
                        sx={{ ml: 0.5, p: 0.25, color: SUBTLE }}
                      >
                        <InfoOutlined sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              }
            />
          )}
          {actualTat != null && (
            <InfoRow
              label='Actual TAT'
              value={
                <Typography
                  component='span'
                  sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: tatColor }}
                >
                  {actualTat} day{actualTat !== 1 ? 's' : ''}
                </Typography>
              }
            />
          )}
        </SectionCard>
      </Grid>

      {/* Shipping & Logistics */}
      <Grid item xs={12} md={4}>
        <SectionCard title='Shipping' sx={{ mb: 0, height: '100%' }}>
          <InfoRow label='Zone' value={orderDetails.merchantZone} />
          <InfoRow
            label='Dead Weight'
            value={sd?.item_weight ? `${(sd.item_weight / 1000).toFixed(2)} kg` : null}
          />
          <InfoRow label='Vol. Weight' value={volWeight ? `${volWeight} kg` : null} hide={isB2B} />
          <InfoRow
            label='Dimensions'
            value={
              sd?.item_length
                ? `${sd.item_length} x ${sd.item_breadth} x ${sd.item_height} cm`
                : null
            }
            hide={isB2B}
          />
          <InfoRow label='Insured' value={orderDetails.isInsured ? 'Yes' : 'No'} />
        </SectionCard>
      </Grid>

      {/* Financial */}
      <Grid item xs={12} md={4}>
        <SectionCard title='Financial' sx={{ mb: 0, height: '100%' }}>
          <InfoRow
            label='Invoice Value'
            value={orderDetails.invoice_value ? numberFormat(orderDetails.invoice_value) : null}
          />
          <InfoRow label='Invoice #' value={orderDetails.invoice_number} />
          <InfoRow
            label='COD Amount'
            value={orderDetails.cod_amount ? numberFormat(orderDetails.cod_amount) : null}
            hide={paymentMode !== 'COD'}
          />
          {mp?.codInvoiceChargeValue != null && paymentMode === 'COD' && (
            <InfoRow label='COD Charge' value={numberFormat(mp.codInvoiceChargeValue)} />
          )}
          <InfoRow
            label='Forward Shipping Cost'
            value={
              mp?.price ? (
                <>
                  {numberFormat(mp.price)}
                  <Tooltip title='Price may change post weight discrepancy'>
                    <Typography
                      component='span'
                      sx={{ color: ERROR, ml: 0.25, fontSize: '10px', verticalAlign: 'super' }}
                    >
                      *
                    </Typography>
                  </Tooltip>
                </>
              ) : null
            }
            hide={!channelAccess?.SHOW_SHIPPING_COST && !isAdmin()}
          />
          <InfoRow
            label='RTO Shipping Cost'
            value={
              orderDetails.merchant_pricing_rto?.price
                ? formatCurrency(orderDetails.merchant_pricing_rto.price)
                : null
            }
            hide={orderDetails.order_type === 'REVERSE SHIPMENT'}
          />
          {insuranceDetails && (
            <InfoRow label='Insurance' value={insuranceDetails?.proposalStatus || 'N/A'} />
          )}
          {orderDetails.paymentModeChange?.length > 0 &&
            orderDetails.paymentModeChange[orderDetails.paymentModeChange.length - 1]
              ?.newPaymentMode?.toUpperCase() === 'PREPAID' && (
              <InfoRow
                label='Payment Change'
                value={
                  <>
                    {orderDetails.paymentModeChange.map((c, i) => {
                      const meta = [
                        c.changedBy ? `by ${c.changedBy}` : null,
                        c.changedAt ? `${parseDateAndTime(c.changedAt, true)}, ${parseTime(c.changedAt)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' \u00b7 ')
                      return (
                        <Typography
                          key={i}
                          component='span'
                          sx={{ display: 'block', mt: i ? 0.5 : 0 }}
                        >
                          <Typography
                            component='span'
                            sx={{ display: 'block', fontFamily: FONT, fontSize: SIZE['2xs'], color: ERROR, fontWeight: WEIGHT.semibold }}
                          >
                            {`${c.existingPaymentMode} \u2192 ${c.newPaymentMode}`}
                          </Typography>
                          {meta && (
                            <Typography
                              component='span'
                              sx={{ display: 'block', fontFamily: FONT, fontSize: '10px', color: SUBTLE, fontWeight: WEIGHT.regular }}
                            >
                              {meta}
                            </Typography>
                          )}
                        </Typography>
                      )
                    })}
                  </>
                }
              />
            )}
        </SectionCard>
      </Grid>
    </Grid>
  )
}
